import { PostEffectBase } from "./PostEffectBase";



export interface BloomSettings {
    bloomThreshold: number,
    blurAmount: number,
    bloomIntensity: number;
}



export class BloomPFx extends PostEffectBase {
    /**
     * (RO) Nastaveni spusteneho efektu
     */
    get settings(): BloomSettings { return this._settings; }
    private _settings: BloomSettings;

    //-------------------------------------------------------------------------
    /**
     * 
     * @param gd 
     * @param camera 
     * @param timer 
     * @param events Pokud nezadano, tak se nevyvola udalost EVENT_COMPLETE
     */
    //-------------------------------------------------------------------------
    constructor(gd: pc.GraphicsDevice, camera: pc.CameraComponent, sampleCnt: number = 14, bufferScale: number = 0.5) {
        super(gd, camera);
        this._postEffect = new PostEffect(this, sampleCnt, bufferScale);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    start(settings: BloomSettings): void {
        this._settings = settings;
        (<PostEffect>this._postEffect).start(settings);
        super._start();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        super.reset();
        this._settings = undefined;
    }
}



class PostEffect extends pc.PostEffect {
    private _passThroughVS: string;

    /**
     * Pixel shader extracts the brighter areas of an image.
     * This is the first step in applying a bloom postprocess.
     */
    private _bloomExtractFS: string;

    /**
     * Pixel shader applies a one dimensional gaussian blur filter.
     * This is used twice by the bloom postprocess, first to blur horizontally, and then again to blur vertically.
     */
    private _gaussianBlurFS: string;

    /**
     * Pixel shader combines the bloom image with the original scene, using tweakable intensity levels.
     * This is the final step in applying a bloom postprocess.
     */
    private _bloomCombineFS: string;

    private _extractShader: pc.Shader;
    private _blurShader: pc.Shader;
    private _combineShader: pc.Shader;

    private _renderTargets: pc.RenderTarget[];

    private _sampleWeights: Float32Array[];
    private _sampleOffsets: Float32Array[];

    private _settings: BloomSettings;

    private _sampleCnt: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(fx: BloomPFx, sampleCnt: number, bufferScale: number) {
        let gd = fx.gd;

        super(gd);
        this._sampleCnt = sampleCnt;

        // Shaders
        let fsPrecision = "precision " + gd.precision + " float;"

        this._passThroughVS = [
            "attribute vec2 aPosition;",
            "varying vec2 vUv0;",
            "",
            "void main(void)",
            "{",
            "    gl_Position = vec4(aPosition, 0.0, 1.0);",
            "    vUv0 = (aPosition + 1.0) * 0.5;",
            "}"
        ].join("\n");

        // Pixel shader extracts the brighter areas of an image.
        // This is the first step in applying a bloom postprocess.
        this._bloomExtractFS = [
            fsPrecision,
            "varying vec2 vUv0;",
            "",
            "uniform sampler2D uBaseTexture;",
            "uniform float uBloomThreshold;",
            "",
            "void main(void) {",
            // Look up the original image color.
            "    vec4 color = texture2D(uBaseTexture, vUv0);",
            "",
            // Adjust it to keep only values brighter than the specified threshold.
            "    gl_FragColor = clamp((color - uBloomThreshold) / (1.0 - uBloomThreshold), 0.0, 1.0);",
            "}"
        ].join("\n");

        // Pixel shader applies a one dimensional gaussian blur filter.
        // This is used twice by the bloom postprocess, first to
        // blur horizontally, and then again to blur vertically.
        this._gaussianBlurFS = [
            fsPrecision,
            "#define SAMPLE_COUNT " + sampleCnt,
            "",
            "varying vec2 vUv0;",
            "",
            "uniform sampler2D uBloomTexture;",
            "uniform vec2 uBlurOffsets[SAMPLE_COUNT];",
            "uniform float uBlurWeights[SAMPLE_COUNT];",
            "",
            "void main(void) {",
            "    vec4 color = vec4(0.0);",
            // Combine a number of weighted image filter taps.
            "    for (int i = 0; i < SAMPLE_COUNT; i++)",
            "    {",
            "        color += texture2D(uBloomTexture, vUv0 + uBlurOffsets[i]) * uBlurWeights[i];",
            "    }",
            "",
            "    gl_FragColor = color;",
            "}"
        ].join("\n");

        // Pixel shader combines the bloom image with the original
        // scene, using tweakable intensity levels.
        // This is the final step in applying a bloom postprocess.
        this._bloomCombineFS = [
            fsPrecision,
            "varying vec2 vUv0;",
            "",
            "uniform float uBloomIntensity;",
            "uniform sampler2D uBaseTexture;",
            "uniform sampler2D uBloomTexture;",
            "",
            "void main(void) {",
            // Look up the bloom and original base image colors.
            "    vec4 bloom = texture2D(uBloomTexture, vUv0) * uBloomIntensity;",
            "    vec4 base = texture2D(uBaseTexture, vUv0);",
            "",
            // Darken down the base image in areas where there is a lot of bloom,
            // to prevent things looking excessively burned-out.
            "    base *= (1.0 - clamp(bloom, 0.0, 1.0));",
            "",
            // Combine the two images.
            "    gl_FragColor = base + bloom;",
            "}"
        ].join("\n");

        let attributes = {
            aPosition: pc.SEMANTIC_POSITION
        };

        this._extractShader = new pc.Shader(gd, {
            attributes: attributes,
            vshader: this._passThroughVS,
            fshader: this._bloomExtractFS
        });

        this._blurShader = new pc.Shader(gd, {
            attributes: attributes,
            vshader: this._passThroughVS,
            fshader: this._gaussianBlurFS
        });

        this._combineShader = new pc.Shader(gd, {
            attributes: attributes,
            vshader: this._passThroughVS,
            fshader: this._bloomCombineFS
        });

        // Render targets
        let bufferW = Math.floor(gd.width * bufferScale);
        let bufferH = Math.floor(gd.height * bufferScale);

        this._renderTargets = [];

        for (let i = 0; i < 2; i++) {
            let colorBuffer = new pc.Texture(gd, {
                format: pc.PIXELFORMAT_R8_G8_B8,
                width: bufferW,
                height: bufferH
            });
            colorBuffer.minFilter = pc.FILTER_LINEAR;
            colorBuffer.magFilter = pc.FILTER_LINEAR;
            colorBuffer.addressU = pc.ADDRESS_CLAMP_TO_EDGE;
            colorBuffer.addressV = pc.ADDRESS_CLAMP_TO_EDGE;

            this._renderTargets.push(new pc.RenderTarget({ colorBuffer: colorBuffer, depth: false }));
        }

        console.log("Bloom post fx buffers: " + this._renderTargets[0].width + "x" + this._renderTargets[1].height);

        // Uniforms
        this._sampleWeights = [];
        this._sampleOffsets = [];

        for (let i = 0; i < 2; i++) {
            this._sampleWeights[i] = new Float32Array(sampleCnt);
            this._sampleOffsets[i] = new Float32Array(sampleCnt * 2);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    start(settings: BloomSettings): void {
        this._settings = settings;

        this.calculateBlurValues(this._sampleWeights[0], this._sampleOffsets[0], 1.0 / this._renderTargets[1].width, 0, settings.blurAmount);
        this.calculateBlurValues(this._sampleWeights[1], this._sampleOffsets[1], 0, 1.0 / this._renderTargets[0].height, settings.blurAmount);

        let device = this.device;
        let scope = device.scope;

        scope.resolve("uBloomThreshold").setValue(settings.bloomThreshold);
        scope.resolve("uBloomIntensity").setValue(settings.bloomIntensity);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    render(inputTarget: pc.RenderTarget, outputTarget: pc.RenderTarget, rect?: pc.Vec4): void {
        let device = this.device;
        let scope = device.scope;

        // Pass 1: draw the scene into rendertarget 1, using a shader that extracts only the brightest parts of the image.        
        scope.resolve("uBaseTexture").setValue(inputTarget.colorBuffer);
        pc.drawFullscreenQuad(device, this._renderTargets[0], this.vertexBuffer, this._extractShader);

        // Pass 2: draw from rendertarget 1 into rendertarget 2, using a shader to apply a horizontal gaussian blur filter.
        scope.resolve("uBlurWeights[0]").setValue(this._sampleWeights[0]);
        scope.resolve("uBlurOffsets[0]").setValue(this._sampleOffsets[0]);
        scope.resolve("uBloomTexture").setValue(this._renderTargets[0].colorBuffer);
        pc.drawFullscreenQuad(device, this._renderTargets[1], this.vertexBuffer, this._blurShader);

        // Pass 3: draw from rendertarget 2 back into rendertarget 1, using a shader to apply a vertical gaussian blur filter.
        scope.resolve("uBlurWeights[0]").setValue(this._sampleWeights[1]);
        scope.resolve("uBlurOffsets[0]").setValue(this._sampleOffsets[1]);
        scope.resolve("uBloomTexture").setValue(this._renderTargets[1].colorBuffer);
        pc.drawFullscreenQuad(device, this._renderTargets[0], this.vertexBuffer, this._blurShader);

        // Pass 4: draw both rendertarget 1 and the original scene image back into the main backbuffer, using a shader that
        // combines them to produce the final bloomed result.        
        scope.resolve("uBloomTexture").setValue(this._renderTargets[0].colorBuffer);
        scope.resolve("uBaseTexture").setValue(inputTarget.colorBuffer);
        pc.drawFullscreenQuad(device, outputTarget, this.vertexBuffer, this._combineShader, rect);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private computeGaussian(n: number, theta: number): number {
        return ((1.0 / Math.sqrt(2 * Math.PI * theta)) * Math.exp(-(n * n) / (2 * theta * theta)));
    }

    //-------------------------------------------------------------------------
    /**
     * Calculates blur
     * @param sampleWeights 
     * @param sampleOffsets 
     * @param dx 
     * @param dy 
     * @param blurAmount 
     */
    //-------------------------------------------------------------------------
    private calculateBlurValues(sampleWeights: Float32Array, sampleOffsets: Float32Array, dx: number, dy: number, blurAmount: number) {
        // Look up how many samples our gaussian blur effect supports.

        // Create temporary arrays for computing our filter settings.
        // The first sample always has a zero offset.
        sampleWeights[0] = this.computeGaussian(0, blurAmount);
        sampleOffsets[0] = 0;
        sampleOffsets[1] = 0;

        // Maintain a sum of all the weighting values.
        let totalWeights = sampleWeights[0];

        // Add pairs of additional sample taps, positioned
        // along a line in both directions from the center.
        let i: number;
        let len: number = Math.floor(this._sampleCnt / 2);

        for (i = 0; i < len; i++) {
            // Store weights for the positive and negative taps.
            let weight = this.computeGaussian(i + 1, blurAmount);
            sampleWeights[i * 2] = weight;
            sampleWeights[i * 2 + 1] = weight;
            totalWeights += weight * 2;

            // To get the maximum amount of blurring from a limited number of
            // pixel shader samples, we take advantage of the bilinear filtering
            // hardware inside the texture fetch unit. If we position our texture
            // coordinates exactly halfway between two texels, the filtering unit
            // will average them for us, giving two samples for the price of one.
            // This allows us to step in units of two texels per sample, rather
            // than just one at a time. The 1.5 offset kicks things off by
            // positioning us nicely in between two texels.
            let sampleOffset = i * 2 + 1.5;

            // Store texture coordinate offsets for the positive and negative taps.
            sampleOffsets[i * 4] = dx * sampleOffset;
            sampleOffsets[i * 4 + 1] = dy * sampleOffset;
            sampleOffsets[i * 4 + 2] = -dx * sampleOffset;
            sampleOffsets[i * 4 + 3] = -dy * sampleOffset;
        }

        // Normalize the list of sample weightings, so they will always sum to one.
        for (i = 0, len = sampleWeights.length; i < len; i++) {
            sampleWeights[i] /= totalWeights;
        }
    }
}